import React, {useState, useEffect, useRef} from 'react';
import {
  ScrollView,
  View,
  Image,
  ImageBackground,
  Text,
  Pressable,
} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {Submission} from '../../Models/PowerSyncModels/models';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {Navigation} from '../../Constants/Navigation';
import {
  METADATA_KEYS,
  COMMON_TEMPLATES,
  CATEGORY,
  PERMISSIONS,
} from '../../Constants/AppConstants';
import RecordList from '../Shared/RecordList';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Images} from '../../Constants/Images';
import {usePermission} from '../../Providers/PermissionProvider';
import {useRelation} from '../../Providers/SubmissionRelationProvider';
import {FilterTypes} from '../../Constants/General';
import ScreenButtons from '../Shared/ScreenButtons';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import {useIsFocused} from '@react-navigation/native';
import {FilterValue} from '../Shared/SearchBox';

export declare type OperationProps = {
  navigation: any;
};

const Operations = (props: OperationProps): React.ReactElement => {
  const [reports, setReports] = useState<Submission[]>([]);
  const [filterValue, setFilterValue] = useState<FilterValue>({
    startDate: '',
    endDate: '',
    statuses: [],
    types: [],
    person: '',
    project: '',
    equipmentType: '',
    equipment: '',
    pendingInvestigation: false,
    investigationAttached: false,
  });

  const {hasPermission} = usePermission();
  const {getRelatedSubmissions} = useRelation();
  const isFocused = useIsFocused();
  const {templateCategories, people, templates, submissions, powersyncReady} = usePowerSyncData();

  const user = usePowerSyncUser();

  const unselectCommand = useRef({
    unselect: () => {},
    setUnselectCommand(func: () => void) {
      this.unselect = func;
    },
  });

  const fetchReports = async () => {
    let viewReport = hasPermission(PERMISSIONS.REPORT_OPERATION_VIEW);

    let templateCategory = templateCategories
      .find(t => t.name === TemplateCategoryTypes.OPERATIONS);
      
    let listTemplateIds = templateCategory?.listTemplateIds ?? [];

    let operationsTemplates = templates
      .filter(
        t =>
          listTemplateIds.includes(t._id)
      )
      .map(t => t.name.toLowerCase().trim());

    let subs = submissions.filter(
      s =>
        operationsTemplates.includes(
          s.templateVersion.name?.toLowerCase().trim() ?? '',
        ),
    );

    //If no view permissions can only see forms that user created or that they can manage
    if (!viewReport) {
      //Special permissions for PM/Supervisor

      let userEmail = user.getUserInfo().email;
      let personLoggedIn = people.find(x => x.email == userEmail);

      if (personLoggedIn) {
        let submissionIds = await getRelatedSubmissions(personLoggedIn._id);
        subs = subs.filter(
          x => submissionIds.includes(x._id) || x.createdBy == userEmail,
        );
      }
    }

    setReports(subs);
  };
  useEffect(() => {
    fetchReports();
  }, [powersyncReady]);

  useEffect(() => {
    if (isFocused) {
      fetchReports();
    }
  }, [isFocused]);

  if (!isFocused) return <></>;

  async function recordPressed(recordId: string): Promise<void> {
    let submission = submissions.find(x => x._id === recordId);

    if (
      submission &&
      submission.templateVersion.name?.toLowerCase().trim() ===
        COMMON_TEMPLATES.TASK.toLowerCase().trim()
    ) {
      if (submission.submissionStatus.name == SubmissionStatuses.DRAFT)
        props.navigation.navigate(Navigation.DYNAMICFORM, {
          submissionId: recordId,
          title: 'Task',
          showConfirmation: false,
        });
      else
        props.navigation.navigate(Navigation.COMPLETIONFORM, {
          submissionId: recordId,
          title: 'Task',
          showConfirmation: false,
        });
    } else
      props.navigation.push(Navigation.DYNAMICFORM, {
        submissionId: recordId,
        showConfirmation: true,
      });
  }

  return (
    <ScrollView style={{backgroundColor: 'white'}}>
      <Pressable
        style={{
          //@ts-ignore
          cursor: 'auto',
        }}
        onPress={() => {
          unselectCommand.current.unselect();
        }}>
        <>
          <View>
            <ImageBackground
              source={Images.OPERATIONS}
              resizeMode='cover'
              style={{height: 260}}>
              <Text style={ReportsStyleSheet.headerTitle}>Operations</Text>
            </ImageBackground>
          </View>
          <View style={{flexDirection: 'row'}}>
            <View style={{flex: 2, padding: 28}}>
              <RecordList
                records={reports}
                showDrafts={true}
                hasMetaData={true}
                category={CATEGORY.OPERATIONS}
                metaDataKey_title={METADATA_KEYS.TITLE}
                metaDataKey_project={METADATA_KEYS.PROJECT_NAME}
                showFilters={true}
                filters={[
                  {label: 'Date Range', type: FilterTypes.DATE_RANGE},
                  {label: 'Type', type: FilterTypes.REPORT_TYPE},
                  {label: 'Project/Site', type: FilterTypes.PROJECT},
                ]}
                navigation={props.navigation}
                onRecordPressed={recordId => recordPressed(recordId)}
                setUnselectCommand={(func: () => void) => {
                  unselectCommand.current.setUnselectCommand(func);
                }}
                filterValue={filterValue}
                onChangeFilter={filterValue => setFilterValue(filterValue)}
              />
            </View>
            <View style={{flex: 1}}>
              <ScreenButtons
                navigation={props.navigation}
                category={TemplateCategoryTypes.OPERATIONS}
                permissions={PERMISSIONS.REPORT_OPERATION_CREATE}
              />
              <View style={CommonStyleSheet.bcsfFooter}>
                <Image
                  source={Images.BCSF}
                  style={CommonStyleSheet.bcsfFooterImage}
                />
                <Text style={CommonStyleSheet.bcsfFooterText}>
                  BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
                </Text>
              </View>
            </View>
          </View>
        </>
      </Pressable>
    </ScrollView>
  );
};

export default Operations;
